import { useCallback, useEffect, useRef, useState } from "react";
import { router, useForm } from "@inertiajs/react";

interface Waiver {
  label: string;
  content_sha256: string;
  legal_text: string;
}

interface Rider {
  position: number;
  adult_name: string;
  minors: string[];
  signed: boolean;
}

interface AdultInput {
  name: string;
  minors: Array<{ name: string }>;
}

interface ContactForm {
  full_name: string;
  email: string;
  phone: string;
  adults: AdultInput[];
}

interface SignatureForm {
  position: number;
  typed_name: string;
  accepted: boolean;
}

interface Props {
  confirmation_reference?: string | null;
  waiver?: Waiver | null;
  riders: Rider[];
  kiosk: boolean;
  kiosk_idle_seconds: number;
  kiosk_warning_seconds: number;
  kiosk_completion_reset_seconds: number;
}

const MAX_ADULTS = 6;
const MAX_MINORS_PER_ADULT = 8;

const colors = {
  navy: "#002868",
  gold: "#ffd400",
  ink: "#172033",
  muted: "#536078",
  line: "#d8e0ee",
  paper: "#ffffff",
  wash: "#f4f7fb",
  softBlue: "#eef4ff",
  success: "#176b43",
  error: "#9b1c1c",
};

function errorText(value: string | string[] | undefined) {
  return Array.isArray(value) ? value.join(" ") : value;
}

function RideRegistration({
  confirmation_reference,
  waiver,
  riders,
  kiosk,
  kiosk_idle_seconds,
  kiosk_warning_seconds,
  kiosk_completion_reset_seconds,
}: Props) {
  const resetInFlight = useRef(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState(kiosk_warning_seconds);
  const [idleCycle, setIdleCycle] = useState(0);
  const contactForm = useForm<ContactForm>({
    full_name: "",
    email: "",
    phone: "",
    adults: [{ name: "", minors: [] }],
  });
  const nextRider = riders.find((rider) => !rider.signed);
  const signatureForm = useForm<SignatureForm>({
    position: nextRider?.position ?? 0,
    typed_name: "",
    accepted: false,
  });
  const contactErrors = contactForm.errors as Record<string, string | string[] | undefined>;
  const signatureErrors = signatureForm.errors as Record<string, string | string[] | undefined>;
  const registrationPath = kiosk ? "/ride_registration?kiosk=1" : "/ride_registration";
  const signaturePath = kiosk ? "/ride_registration/signatures?kiosk=1" : "/ride_registration/signatures";

  const resetForNextCustomer = useCallback(() => {
    if (resetInFlight.current) return;
    resetInFlight.current = true;
    router.post(
      kiosk ? "/ride_registration/reset?kiosk=1" : "/ride_registration/reset",
      {},
      {
        replace: true,
        preserveScroll: false,
        preserveState: false,
        onFinish: () => { resetInFlight.current = false; },
      },
    );
  }, [kiosk]);

  useEffect(() => {
    if (!nextRider) return;
    signatureForm.setData({
      position: nextRider.position,
      typed_name: "",
      accepted: false,
    });
  }, [nextRider?.position]);

  useEffect(() => {
    if (!kiosk || confirmation_reference) return;

    let idleTimer: number | undefined;
    let resetTimer: number | undefined;
    let countdownTimer: number | undefined;

    const clearTimers = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      if (resetTimer) window.clearTimeout(resetTimer);
      if (countdownTimer) window.clearInterval(countdownTimer);
    };
    const armIdleReset = () => {
      clearTimers();
      setIdleWarning(false);
      setIdleSecondsRemaining(kiosk_warning_seconds);
      idleTimer = window.setTimeout(() => {
        setIdleWarning(true);
        let remaining = kiosk_warning_seconds;
        countdownTimer = window.setInterval(() => {
          remaining = Math.max(remaining - 1, 0);
          setIdleSecondsRemaining(remaining);
        }, 1_000);
        resetTimer = window.setTimeout(resetForNextCustomer, kiosk_warning_seconds * 1_000);
      }, kiosk_idle_seconds * 1_000);
    };
    const recordActivity = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-kiosk-idle-warning]")) return;
      armIdleReset();
    };

    const activityEvents = ["pointerdown", "keydown", "input", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, recordActivity));
    armIdleReset();

    return () => {
      clearTimers();
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
    };
  }, [confirmation_reference, idleCycle, kiosk, kiosk_idle_seconds, kiosk_warning_seconds, resetForNextCustomer]);

  useEffect(() => {
    if (!kiosk || !confirmation_reference) return;

    const timer = window.setTimeout(resetForNextCustomer, kiosk_completion_reset_seconds * 1_000);
    return () => window.clearTimeout(timer);
  }, [confirmation_reference, kiosk, kiosk_completion_reset_seconds, resetForNextCustomer]);

  const fieldStyle: React.CSSProperties = {
    boxSizing: "border-box",
    width: "100%",
    minHeight: "48px",
    border: `1.5px solid ${colors.line}`,
    borderRadius: "10px",
    padding: "12px 14px",
    background: colors.paper,
    color: colors.ink,
    font: "inherit",
    fontSize: "16px",
  };

  const labelStyle: React.CSSProperties = {
    display: "grid",
    gap: "7px",
    color: colors.ink,
    fontSize: "15px",
    fontWeight: 750,
  };

  const primaryButtonStyle: React.CSSProperties = {
    minHeight: "50px",
    border: 0,
    borderRadius: "10px",
    padding: "13px 20px",
    background: colors.gold,
    color: colors.navy,
    cursor: "pointer",
    font: "inherit",
    fontSize: "17px",
    fontWeight: 850,
  };

  const secondaryButtonStyle: React.CSSProperties = {
    minHeight: "46px",
    border: `1.5px solid ${colors.navy}`,
    borderRadius: "10px",
    padding: "10px 14px",
    background: colors.paper,
    color: colors.navy,
    cursor: "pointer",
    font: "inherit",
    fontSize: "16px",
    fontWeight: 750,
  };

  function updateContactName(fullName: string) {
    const previousName = contactForm.data.full_name.trim();
    const adults = contactForm.data.adults.map((adult, index) => {
      if (index !== 0 || (adult.name.trim() && adult.name.trim() !== previousName)) return adult;
      return { ...adult, name: fullName };
    });
    contactForm.setData({ ...contactForm.data, full_name: fullName, adults });
  }

  function updateAdult(adultIndex: number, updates: Partial<AdultInput>) {
    contactForm.setData("adults", contactForm.data.adults.map((adult, index) => (
      index === adultIndex ? { ...adult, ...updates } : adult
    )));
  }

  function addAdult() {
    if (contactForm.data.adults.length >= MAX_ADULTS) return;
    contactForm.setData("adults", [...contactForm.data.adults, { name: "", minors: [] }]);
  }

  function removeAdult(adultIndex: number) {
    contactForm.setData("adults", contactForm.data.adults.filter((_, index) => index !== adultIndex));
  }

  function addMinor(adultIndex: number) {
    const adult = contactForm.data.adults[adultIndex];
    if (adult.minors.length >= MAX_MINORS_PER_ADULT) return;
    updateAdult(adultIndex, { minors: [...adult.minors, { name: "" }] });
  }

  function updateMinor(adultIndex: number, minorIndex: number, name: string) {
    const adult = contactForm.data.adults[adultIndex];
    updateAdult(adultIndex, {
      minors: adult.minors.map((minor, index) => index === minorIndex ? { name } : minor),
    });
  }

  function removeMinor(adultIndex: number, minorIndex: number) {
    const adult = contactForm.data.adults[adultIndex];
    updateAdult(adultIndex, {
      minors: adult.minors.filter((_, index) => index !== minorIndex),
    });
  }

  function submitDetails(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    contactForm.post(registrationPath, { preserveScroll: true });
  }

  function submitSignature(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!nextRider) return;
    signatureForm.transform((data) => ({ ...data, position: nextRider.position }));
    signatureForm.post(signaturePath, {
      preserveScroll: true,
      onSuccess: () => signatureForm.reset(),
    });
  }

  return (
    <div
      data-ride-registration
      data-kiosk-mode={kiosk || undefined}
      style={{
        minHeight: "100vh",
        background: colors.wash,
        color: colors.ink,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <header style={{ background: colors.navy, color: "white", padding: "18px 20px" }}>
        <div style={{ maxWidth: "620px", margin: "0 auto", fontSize: "17px", fontWeight: 850 }}>
          {/* Same Rails app as the main site. On a personal phone the wordmark
              goes home; kiosk mode stays a dead end so the next walk-in cannot
              wander the shop iPad off the form. */}
          {kiosk ? (
            "Front Range Cargo Bikes"
          ) : (
            <a href="/" style={{ color: "inherit", textDecoration: "none" }}>
              Front Range Cargo Bikes
            </a>
          )}
        </div>
      </header>

      <main style={{ boxSizing: "border-box", width: "100%", maxWidth: "660px", margin: "0 auto", padding: "28px 18px 56px" }}>
        <section
          style={{
            border: `1px solid ${colors.line}`,
            borderRadius: "16px",
            padding: "clamp(22px, 6vw, 34px)",
            background: colors.paper,
            boxShadow: "0 8px 28px rgba(23,32,51,.08)",
            overflowWrap: "anywhere",
          }}
        >
          <h1 style={{ margin: 0, color: colors.navy, fontSize: "clamp(30px, 9vw, 42px)", lineHeight: 1.08 }}>
            Ride registration
          </h1>
          <p style={{ margin: "14px 0 0", color: colors.muted, fontSize: "17px", lineHeight: 1.55 }}>
            Share your contact and rider details with the shop before your ride. This form does not reserve a time.
          </p>
          <p
            data-ride-preparation
            style={{
              margin: "18px 0 0",
              borderLeft: `5px solid ${colors.gold}`,
              padding: "3px 0 3px 14px",
              color: colors.ink,
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            Bring helmets for everyone riding. Closed-toe shoes, gloves, and glasses are recommended.
          </p>
          {kiosk && (
            <p data-kiosk-session-note style={{ margin: "12px 0 0", color: colors.muted, fontSize: "14px", lineHeight: 1.5 }}>
              Shared-device mode. This form clears after inactivity.
            </p>
          )}

          {idleWarning && (
            <div
              data-kiosk-idle-warning
              role="alert"
              style={{ display: "grid", gap: "14px", marginTop: "22px", border: `2px solid ${colors.gold}`, borderRadius: "12px", padding: "16px", background: "#fffbea" }}
            >
              <div>
                <strong style={{ display: "block", color: colors.navy, fontSize: "18px" }}>Still using this form?</strong>
                <span style={{ display: "block", marginTop: "5px", color: colors.muted, lineHeight: 1.5 }}>
                  This shared-device session clears in {idleSecondsRemaining} seconds.
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <button type="button" onClick={() => setIdleCycle((cycle) => cycle + 1)} style={secondaryButtonStyle}>
                  Continue registration
                </button>
                <button type="button" onClick={resetForNextCustomer} style={primaryButtonStyle}>
                  Start over
                </button>
              </div>
            </div>
          )}

          {confirmation_reference ? (
            <div role="status" aria-live="polite" style={{ marginTop: "28px", borderLeft: `6px solid ${colors.gold}`, padding: "4px 0 4px 18px" }}>
              <h2 tabIndex={-1} style={{ margin: 0, color: colors.navy, fontSize: "24px" }}>Details and waiver saved</h2>
              <p style={{ margin: "10px 0 0", fontSize: "17px", lineHeight: 1.55 }}>
                Please see a team member before riding.
              </p>
              <p style={{ margin: "10px 0 0", color: colors.muted, fontSize: "14px" }}>
                Reference: <strong>{confirmation_reference}</strong>
              </p>
              {kiosk && (
                <button type="button" onClick={resetForNextCustomer} style={{ ...primaryButtonStyle, marginTop: "18px", width: "100%" }}>
                  Next customer
                </button>
              )}
            </div>
          ) : !waiver ? (
            <div role="alert" style={{ marginTop: "26px", color: colors.error, fontSize: "16px", lineHeight: 1.55 }}>
              The waiver is not available. Please see a team member.
              {errorText(contactErrors.waiver) && <span style={{ display: "block" }}>{errorText(contactErrors.waiver)}</span>}
            </div>
          ) : riders.length === 0 ? (
            <form onSubmit={submitDetails} noValidate style={{ display: "grid", gap: "20px", marginTop: "28px" }}>
              <h2 style={{ margin: 0, color: colors.navy, fontSize: "23px" }}>Contact details</h2>
              <label style={labelStyle}>
                Contact full name
                <input
                  name="full_name"
                  value={contactForm.data.full_name}
                  onChange={(event) => updateContactName(event.target.value)}
                  autoComplete="name"
                  maxLength={120}
                  required
                  style={fieldStyle}
                />
                {contactForm.errors.full_name && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(contactForm.errors.full_name)}</span>}
              </label>

              <label style={labelStyle}>
                Email
                <input
                  name="email"
                  type="email"
                  value={contactForm.data.email}
                  onChange={(event) => contactForm.setData("email", event.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  maxLength={254}
                  required
                  style={fieldStyle}
                />
                {contactForm.errors.email && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(contactForm.errors.email)}</span>}
              </label>

              <label style={labelStyle}>
                Mobile phone
                <input
                  name="phone"
                  type="tel"
                  value={contactForm.data.phone}
                  onChange={(event) => contactForm.setData("phone", event.target.value)}
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={40}
                  required
                  style={fieldStyle}
                />
                {contactForm.errors.phone && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(contactForm.errors.phone)}</span>}
              </label>

              <fieldset style={{ display: "grid", gap: "16px", margin: "4px 0 0", border: 0, padding: 0 }}>
                <legend style={{ marginBottom: "8px", color: colors.navy, fontSize: "23px", fontWeight: 800 }}>People riding</legend>
                <p style={{ margin: 0, color: colors.muted, fontSize: "15px", lineHeight: 1.5 }}>
                  Every adult signs separately. List each child under the adult responsible for them.
                </p>

                {contactForm.data.adults.map((adult, adultIndex) => (
                  <div
                    data-adult-rider
                    key={adultIndex}
                    style={{ display: "grid", gap: "14px", border: `1px solid ${colors.line}`, borderRadius: "12px", padding: "16px", background: colors.softBlue }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                      <strong style={{ color: colors.navy }}>Adult rider {adultIndex + 1}</strong>
                      {contactForm.data.adults.length > 1 && (
                        <button type="button" onClick={() => removeAdult(adultIndex)} style={{ border: 0, background: "transparent", color: colors.navy, font: "inherit", fontWeight: 750, textDecoration: "underline", cursor: "pointer" }}>
                          Remove adult
                        </button>
                      )}
                    </div>
                    <label style={labelStyle}>
                      Adult rider {adultIndex + 1} full legal name
                      <input
                        name={`adults[${adultIndex}][name]`}
                        value={adult.name}
                        onChange={(event) => updateAdult(adultIndex, { name: event.target.value })}
                        autoComplete="name"
                        maxLength={120}
                        required
                        style={fieldStyle}
                      />
                    </label>

                    {adult.minors.map((minor, minorIndex) => (
                      <div key={minorIndex} style={{ display: "grid", gap: "8px" }}>
                        <label style={labelStyle}>
                          Child {minorIndex + 1} full name
                          <input
                            name={`adults[${adultIndex}][minors][${minorIndex}][name]`}
                            value={minor.name}
                            onChange={(event) => updateMinor(adultIndex, minorIndex, event.target.value)}
                            maxLength={120}
                            required
                            style={fieldStyle}
                          />
                        </label>
                        <button type="button" onClick={() => removeMinor(adultIndex, minorIndex)} style={{ justifySelf: "start", border: 0, background: "transparent", color: colors.navy, font: "inherit", fontWeight: 750, textDecoration: "underline", cursor: "pointer" }}>
                          Remove child
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addMinor(adultIndex)}
                      disabled={adult.minors.length >= MAX_MINORS_PER_ADULT}
                      style={{ ...secondaryButtonStyle, opacity: adult.minors.length >= MAX_MINORS_PER_ADULT ? 0.55 : 1 }}
                    >
                      Add a child
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addAdult}
                  disabled={contactForm.data.adults.length >= MAX_ADULTS}
                  style={{ ...secondaryButtonStyle, opacity: contactForm.data.adults.length >= MAX_ADULTS ? 0.55 : 1 }}
                >
                  Add another adult
                </button>
                {contactForm.errors.adults && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(contactForm.errors.adults)}</span>}
              </fieldset>

              <button
                type="submit"
                disabled={contactForm.processing}
                style={{ ...primaryButtonStyle, cursor: contactForm.processing ? "wait" : "pointer", opacity: contactForm.processing ? 0.7 : 1 }}
              >
                {contactForm.processing ? "Saving…" : "Continue to waiver"}
              </button>
            </form>
          ) : (
            <section data-waiver-step style={{ display: "grid", gap: "20px", marginTop: "28px" }}>
              <div>
                <h2 style={{ margin: 0, color: colors.navy, fontSize: "26px" }}>Ride waiver</h2>
                <p style={{ margin: "8px 0 0", color: colors.muted, fontSize: "14px" }}>Version {waiver.label}</p>
              </div>

              <div
                data-waiver-legal-text
                tabIndex={0}
                style={{
                  maxHeight: "360px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  border: `1px solid ${colors.line}`,
                  borderRadius: "10px",
                  padding: "18px",
                  background: colors.wash,
                  color: colors.ink,
                  fontSize: "15px",
                  lineHeight: 1.6,
                }}
              >
                {waiver.legal_text}
              </div>

              <ol aria-label="Adult waiver status" style={{ display: "grid", gap: "8px", margin: 0, paddingLeft: "22px" }}>
                {riders.map((rider) => (
                  <li key={rider.position} style={{ paddingLeft: "4px", fontSize: "16px" }}>
                    <span>{rider.adult_name}</span>{" "}
                    <strong style={{ color: rider.signed ? colors.success : colors.muted }}>
                      {rider.signed ? "Signed" : "Needs signature"}
                    </strong>
                  </li>
                ))}
              </ol>

              {nextRider && (
                <form onSubmit={submitSignature} noValidate style={{ display: "grid", gap: "18px", borderTop: `1px solid ${colors.line}`, paddingTop: "20px" }}>
                  <h3 style={{ margin: 0, color: colors.navy, fontSize: "21px" }}>{nextRider.adult_name} signs now</h3>
                  {nextRider.minors.length > 0 && (
                    <p style={{ margin: 0, color: colors.ink, fontSize: "15px", lineHeight: 1.5 }}>
                      Children covered by this adult: <strong>{nextRider.minors.join(", ")}</strong>
                    </p>
                  )}
                  <label style={labelStyle}>
                    Type your full legal name
                    <input
                      name="typed_name"
                      value={signatureForm.data.typed_name}
                      onChange={(event) => signatureForm.setData("typed_name", event.target.value)}
                      autoComplete="name"
                      maxLength={120}
                      required
                      style={fieldStyle}
                    />
                    {signatureForm.errors.typed_name && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(signatureForm.errors.typed_name)}</span>}
                  </label>

                  <label style={{ display: "grid", gridTemplateColumns: "24px 1fr", gap: "11px", alignItems: "start", color: colors.ink, fontSize: "16px", lineHeight: 1.45, cursor: "pointer" }}>
                    <input
                      name="accepted"
                      type="checkbox"
                      checked={signatureForm.data.accepted}
                      onChange={(event) => signatureForm.setData("accepted", event.target.checked)}
                      required
                      style={{ width: "22px", height: "22px", margin: "1px 0 0" }}
                    />
                    <span>I have read and agree to the waiver shown above.</span>
                  </label>
                  {signatureForm.errors.accepted && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(signatureForm.errors.accepted)}</span>}
                  {signatureForm.errors.position && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(signatureForm.errors.position)}</span>}
                  {signatureErrors.waiver && <span role="alert" style={{ color: colors.error, fontSize: "14px", fontWeight: 500 }}>{errorText(signatureErrors.waiver)}</span>}

                  <button
                    type="submit"
                    disabled={signatureForm.processing}
                    style={{ ...primaryButtonStyle, cursor: signatureForm.processing ? "wait" : "pointer", opacity: signatureForm.processing ? 0.7 : 1 }}
                  >
                    {signatureForm.processing ? "Saving signature…" : `Sign as ${nextRider.adult_name}`}
                  </button>
                </form>
              )}
            </section>
          )}
        </section>
      </main>
    </div>
  );
}

export default RideRegistration;
